import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../../services/api.service';
import { ApiResponse } from '../../../shared/types';
import {
  ImpersonateAdminApiData,
  ImpersonationSessionPayload,
  ImpersonationTargetType,
  ImpersonationStaffListParams,
  ImpersonationStaffListResult,
  ImpersonationStaffMember,
  ImpersonationTunnel,
  ImpersonationTunnelListParams,
  ImpersonationTunnelListResult,
} from '../models/impersonation.models';
import {
  extractListRows,
  readResponsePagination,
  resolveListPagination,
} from '../../../shared/utils/list-pagination.util';

/**
 * Impersonation targets and session creation.
 *
 * The company list itself is not here — impersonation reads companies through
 * CompanyService, the single client for `admin/super-admin/companies`.
 */
@Injectable({
  providedIn: 'root',
})
export class ImpersonationApiService {
  private readonly api = inject(ApiService);

  /**
   * Company employees list:
   * GET admin/super-admin/companies/:company_id/employees
   * Query: page, limit, search, location_id, emp_status
   */
  getShopStaff(
    shopId: string,
    params: ImpersonationStaffListParams = {}
  ): Observable<ApiResponse<ImpersonationStaffListResult>> {
    const query: Record<string, string | number | boolean> = {};
    if (params.page != null) query['page'] = params.page;
    if (params.limit != null) query['limit'] = params.limit;
    if (params.search?.trim()) query['search'] = params.search.trim();
    if (params.location_id?.trim()) query['location_id'] = params.location_id.trim();
    if (params.emp_status !== undefined && params.emp_status !== null && params.emp_status !== '') {
      query['emp_status'] = params.emp_status;
    }

    return this.api
      .getWithSuperAdminToken<any>(`admin/super-admin/companies/${shopId}/employees`, query)
      .pipe(
        map(res => ({
          ...res,
          data: this.normalizeStaffList(res.data, params, readResponsePagination(res)),
        }))
      );
  }

  /**
   * Company tunnel users list:
   * GET admin/super-admin/companies/:company_id/tunnel-users
   * Query: page (default 1), limit (default 25), search (branch, email, city, contact)
   */
  getShopTunnels(
    shopId: string,
    params: ImpersonationTunnelListParams = {}
  ): Observable<ApiResponse<ImpersonationTunnelListResult>> {
    const query: Record<string, string | number> = {};
    if (params.page != null) query['page'] = params.page;
    if (params.limit != null) query['limit'] = params.limit;
    if (params.search?.trim()) query['search'] = params.search.trim();

    return this.api
      .getWithSuperAdminToken<any>(`admin/super-admin/companies/${shopId}/tunnel-users`, query)
      .pipe(
        map(res => ({
          ...res,
          data: this.normalizeTunnelList(res.data, params, readResponsePagination(res)),
        }))
      );
  }

  /**
   * Impersonate shop admin:
   * POST admin/super-admin/impersonate/admin
   * Body: { user_id, company_id?, reason? }
   */
  impersonateAdmin(body: {
    user_id: string;
    company_id?: string;
    reason?: string;
  }): Observable<ApiResponse<ImpersonateAdminApiData>> {
    return this.api.postWithSuperAdminToken<ImpersonateAdminApiData>(
      'admin/super-admin/impersonate/admin',
      body
    );
  }

  /**
   * Impersonate staff/employee:
   * POST admin/super-admin/impersonate/staff
   * Body: { user_id, company_id?, reason? }
   */
  impersonateStaff(body: {
    user_id: string;
    company_id?: string;
    reason?: string;
  }): Observable<ApiResponse<ImpersonateAdminApiData>> {
    return this.api.postWithSuperAdminToken<ImpersonateAdminApiData>(
      'admin/super-admin/impersonate/staff',
      body
    );
  }

  /**
   * Impersonate tunnel display:
   * POST admin/super-admin/impersonate/tunnel
   * Body: { location_id, company_id?, reason? }
   */
  impersonateTunnel(body: {
    location_id: string;
    company_id?: string;
    reason?: string;
  }): Observable<ApiResponse<ImpersonateAdminApiData>> {
    return this.api.postWithSuperAdminToken<ImpersonateAdminApiData>(
      'admin/super-admin/impersonate/tunnel',
      body
    );
  }

  /** @deprecated Legacy endpoint removed — use impersonateAdmin / impersonateStaff / impersonateTunnel. */
  impersonate(body: {
    company_id: string;
    target_type: ImpersonationTargetType;
    user_id?: string;
    location_id?: string;
  }): Observable<ApiResponse<ImpersonationSessionPayload>> {
    throw new Error('Legacy impersonation endpoint is no longer available');
  }

  private normalizeStaffList(
    data: any,
    params: ImpersonationStaffListParams,
    paginationFromResponse: Record<string, any> | null = null
  ): ImpersonationStaffListResult {
    const page = params.page ?? 1;
    const limit = params.limit ?? 20;
    if (!data) {
      return {
        staff: [],
        totalItems: 0,
        currentPage: page,
        totalPages: 0,
        itemsPerPage: limit,
      };
    }

    const { rows, pagination: nestedPagination } = extractListRows(data, [
      'employees',
      'staff',
      'data',
      'users',
    ]);
    const staff = rows.map(row => this.toStaffMember(row as Record<string, any>));
    return {
      staff,
      ...resolveListPagination(
        nestedPagination || paginationFromResponse,
        { page, limit },
        staff.length
      ),
    };
  }

  private normalizeTunnelList(
    data: any,
    params: ImpersonationTunnelListParams,
    paginationFromResponse: Record<string, any> | null = null
  ): ImpersonationTunnelListResult {
    const page = params.page ?? 1;
    const limit = params.limit ?? 25;
    if (!data) {
      return {
        tunnels: [],
        totalItems: 0,
        currentPage: page,
        totalPages: 0,
        itemsPerPage: limit,
      };
    }

    const { rows, pagination: nestedPagination } = extractListRows(data, [
      'tunnel_users',
      'tunnelUsers',
      'tunnels',
      'users',
      'data',
    ]);
    const tunnels = rows.map(row => this.toTunnel(row as Record<string, any>));
    return {
      tunnels,
      ...resolveListPagination(
        nestedPagination || paginationFromResponse,
        { page, limit },
        tunnels.length
      ),
    };
  }

  private toTunnel(row: Record<string, any>): ImpersonationTunnel {
    const location =
      row['location'] && typeof row['location'] === 'object'
        ? (row['location'] as Record<string, any>)
        : null;

    const id = row['user_id'] ?? row['id'] ?? row['tunnel_id'] ?? '';
    const userId = row['user_id'] ?? row['id'];

    const isActive =
      typeof row['is_active'] === 'boolean'
        ? row['is_active']
        : row['is_active'] === 'true' || row['is_active'] === 1 || row['is_active'] === '1'
          ? true
          : row['is_active'] === 'false' || row['is_active'] === 0 || row['is_active'] === '0'
            ? false
            : undefined;

    return {
      id: String(id),
      user_id: userId != null ? String(userId) : undefined,
      name:
        row['name'] != null && String(row['name']).trim() ? String(row['name']).trim() : undefined,
      location_id: String(row['location_id'] ?? location?.['id'] ?? row['location'] ?? ''),
      location_name:
        row['location_name'] != null
          ? String(row['location_name'])
          : location?.['name'] != null
            ? String(location['name'])
            : location?.['branch'] != null
              ? String(location['branch'])
              : undefined,
      branch:
        row['branch'] != null
          ? String(row['branch'])
          : location?.['branch'] != null
            ? String(location['branch'])
            : undefined,
      email: row['email'] != null ? String(row['email']) : undefined,
      city:
        row['city'] != null
          ? String(row['city'])
          : location?.['city'] != null
            ? String(location['city'])
            : undefined,
      contact:
        row['contact'] != null
          ? String(row['contact'])
          : row['phone'] != null
            ? String(row['phone'])
            : row['contact_number'] != null
              ? String(row['contact_number'])
              : undefined,
      is_active: isActive,
    };
  }

  private toStaffMember(row: Record<string, any>): ImpersonationStaffMember {
    const first = row['first_name'] != null ? String(row['first_name']) : '';
    const last = row['last_name'] != null ? String(row['last_name']) : '';
    const combinedName = `${first} ${last}`.trim();
    const name =
      row['name'] != null && String(row['name']).trim()
        ? String(row['name']).trim()
        : combinedName || String(row['email'] ?? 'Employee');

    const empStatus =
      typeof row['emp_status'] === 'boolean'
        ? row['emp_status']
        : row['emp_status'] === 'true' || row['emp_status'] === 1 || row['emp_status'] === '1'
          ? true
          : row['emp_status'] === 'false' || row['emp_status'] === 0 || row['emp_status'] === '0'
            ? false
            : typeof row['is_active'] === 'boolean'
              ? row['is_active']
              : undefined;

    const id = row['user_id'] ?? row['id'] ?? row['emp_id'] ?? row['employee_id'] ?? '';
    const userId = row['user_id'] ?? row['id'];

    const location =
      row['location'] && typeof row['location'] === 'object'
        ? (row['location'] as Record<string, any>)
        : null;

    const designation = row['designation'] != null ? String(row['designation']) : '';
    const roleRaw = String(row['role'] || designation || 'attendant').toLowerCase();
    const role = roleRaw.includes('admin')
      ? 'admin'
      : roleRaw.includes('attendant') || roleRaw.includes('staff')
        ? 'attendant'
        : roleRaw || 'attendant';

    return {
      id: String(id),
      user_id: userId != null ? String(userId) : undefined,
      name,
      email: row['email'] != null ? String(row['email']) : '',
      role,
      location_id:
        row['location_id'] != null
          ? String(row['location_id'])
          : location?.['id'] != null
            ? String(location['id'])
            : undefined,
      location_name:
        row['location_name'] != null
          ? String(row['location_name'])
          : row['branch'] != null
            ? String(row['branch'])
            : location?.['branch'] != null
              ? String(location['branch'])
              : location?.['name'] != null
                ? String(location['name'])
                : undefined,
      is_active: empStatus,
      emp_status: empStatus,
      first_name: first || undefined,
      last_name: last || undefined,
      designation: designation || undefined,
      picture_url: row['picture_url'] != null ? String(row['picture_url']) : null,
    };
  }
}
